import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../lib/auth';
import { ArrowRight, FileSearch, Trash2, Archive, ArchiveRestore, Loader2 } from 'lucide-react';
import { SeverityBadge } from '../components/SeverityBadge';
import api from '../lib/api';
import { toast } from 'sonner';

const StatusPill = ({ status }) => {
  const inProgress = status === 'processing' || status === 'preanalyzed' || status === 'uploaded';
  if (status === 'complete') {
    return <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-foreground border border-border px-2 py-1">Complete</span>;
  }
  if (status === 'error') {
    return <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-severity-high border border-severity-high px-2 py-1">Error</span>;
  }
  if (inProgress) {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border-gradient-pt px-2 py-1" data-testid="status-in-progress">
        <Loader2 size={11} className="animate-spin" strokeWidth={1.8} />
        In progress
      </span>
    );
  }
  return <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{status || '—'}</span>;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/audit', { params: { include_archived: showArchived } });
      setItems(data.items || []);
    } catch {
      /* ignore */
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load, refreshTick]);

  // Auto-poll while any audit is still in progress, so the live indicator updates.
  useEffect(() => {
    const hasInProgress = items.some((a) => a.status === 'processing');
    if (!hasInProgress) return;
    const t = setInterval(() => setRefreshTick((n) => n + 1), 4000);
    return () => clearInterval(t);
  }, [items]);

  const onDelete = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Permanently delete this audit? This cannot be undone.')) return;
    try {
      await api.delete(`/audit/${id}`);
      toast.success('Audit deleted');
      setItems((xs) => xs.filter((x) => x.audit_id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete');
    }
  };

  const onArchiveToggle = async (e, a) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await api.patch(`/audit/${a.audit_id}/archive`);
      toast.success(data.archived ? 'Audit archived' : 'Audit restored');
      // If we're not viewing archived items, drop it; otherwise just update the flag.
      setItems((xs) => {
        if (!showArchived && data.archived) return xs.filter((x) => x.audit_id !== a.audit_id);
        return xs.map((x) => x.audit_id === a.audit_id ? { ...x, archived: data.archived } : x);
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update');
    }
  };

  const visibleCount = items.length;

  return (
    <Layout>
      {user?.role === 'super_admin' && <Navigate to="/admin" replace />}
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8 md:mb-10">
          <div>
            <div className="label-mono">PackCheck / Workspace</div>
            <h1 className="text-2xl md:text-4xl font-medium tracking-tight mt-2">Welcome, {user?.name || user?.email}</h1>
            <p className="text-muted-foreground mt-2 text-sm md:text-base">{visibleCount} {showArchived ? 'audits (incl. archived)' : 'active audits'} in your workspace.</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setShowArchived((v) => !v)}
              data-testid="toggle-archived"
              className="inline-flex items-center gap-2 h-11 px-3 sm:px-4 border border-border hover:bg-surface-hover text-sm"
            >
              {showArchived ? <ArchiveRestore size={14} strokeWidth={1.5} /> : <Archive size={14} strokeWidth={1.5} />}
              <span className="hidden sm:inline">{showArchived ? 'Hide archived' : 'Show archived'}</span>
              <span className="sm:hidden">{showArchived ? 'Hide' : 'Archived'}</span>
            </button>
            <Link to="/audit/new" data-testid="dashboard-new-audit" className="inline-flex items-center gap-2 h-11 px-3 sm:px-5 bg-foreground text-background hover:opacity-90 text-sm">
              <span className="hidden sm:inline">New audit</span><span className="sm:hidden">New</span> <ArrowRight size={15} strokeWidth={1.5} />
            </Link>
          </div>
        </div>

        <div className="border border-border scroll-table" data-testid="audit-history-table">
          <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
            <div className="col-span-3">File</div>
            <div className="col-span-2">Format</div>
            <div className="col-span-1 text-right">Score</div>
            <div className="col-span-2">Findings</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-right">Date</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {items.length === 0 && (
            <div className="p-12 text-center">
              <FileSearch size={28} className="mx-auto text-muted-foreground" strokeWidth={1.5} />
              <p className="mt-4 text-sm text-muted-foreground">{showArchived ? 'No archived audits.' : 'No audits yet.'}</p>
              <Link to="/audit/new" className="mt-4 inline-flex items-center gap-1 text-gradient-pt font-medium">Run your first audit →</Link>
            </div>
          )}
          {items.map((a) => {
            const isDraft = a.status === 'preanalyzed' || a.status === 'uploaded';
            const inProgress = a.status === 'processing';
            // Drafts → resume the wizard with the audit_id; in-progress → audit page (polls); else → report
            const href = isDraft
              ? `/audit/new?resume=${a.audit_id}`
              : `/audit/${a.audit_id}`;
            return (
              <Link
                to={href}
                key={a.audit_id}
                className={`grid grid-cols-12 px-4 py-4 border-t border-border text-sm items-center hover:bg-surface-hover transition-colors ${a.archived ? 'opacity-60' : ''}`}
                data-testid={`history-row-${a.audit_id}`}
              >
                <div className="col-span-3">
                  <div className="font-medium flex items-center gap-2">
                    {a.file_name}
                    {a.archived && <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5">Archived</span>}
                    {isDraft && <span className="font-mono text-[9px] uppercase tracking-widest text-accent border border-accent px-1.5 py-0.5">Draft</span>}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-1">{a.audit_id}</div>
                </div>
                <div className="col-span-2 text-sm">{a.pack_config?.pack_format_name || '—'}</div>
                <div className="col-span-1 font-mono text-right">{a.overall_score || '—'}</div>
                <div className="col-span-2 flex gap-1.5 flex-wrap">
                  {!!a.critical_count && <SeverityBadge severity="CRITICAL" count={a.critical_count} />}
                  {!!a.high_count && <SeverityBadge severity="HIGH" count={a.high_count} />}
                  {!!a.medium_count && <SeverityBadge severity="MEDIUM" count={a.medium_count} />}
                  {!!a.low_count && <SeverityBadge severity="LOW" count={a.low_count} />}
                  {(inProgress || isDraft) && a.progress_message && (
                    <span className="font-mono text-[10px] text-muted-foreground">{a.progress_message}</span>
                  )}
                </div>
                <div className="col-span-2"><StatusPill status={a.status} /></div>
                <div className="col-span-1 text-right font-mono text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <button
                    onClick={(e) => onArchiveToggle(e, a)}
                    title={a.archived ? 'Restore' : 'Archive'}
                    data-testid={`archive-btn-${a.audit_id}`}
                    className="w-8 h-8 grid place-items-center border border-border hover:bg-surface-hover"
                  >
                    {a.archived ? <ArchiveRestore size={13} strokeWidth={1.5} /> : <Archive size={13} strokeWidth={1.5} />}
                  </button>
                  <button
                    onClick={(e) => onDelete(e, a.audit_id)}
                    title="Delete"
                    data-testid={`delete-btn-${a.audit_id}`}
                    className="w-8 h-8 grid place-items-center border border-border hover:bg-severity-high hover:text-background"
                  >
                    <Trash2 size={13} strokeWidth={1.5} />
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
